import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import LayoutWithSidebar from '../common/layoutWithSidebar';
import InitialAnalytics from '../UserJourny/InitialAnalytics';
import AssistantDefination from '../UserJourny/AssistantDefination';
import Personalization from '../UserJourny/Personalization';
import ReservationSetup from './ReservationSetup';
import ReservationSidebar from './ReservationSidebar';
import { useSaveAssistant } from '../../hooks/useSaveAssistant';
import { useUser } from '../../hooks/useUser';
import { useAgents } from '../../hooks/useAgents';

const ReservationFlow = () => {
  const [selectedSection, setSelectedSection] = useState<string>(
    'reservation-system-setup'
  );
  const [selectedSubSection, setSelectedSubSection] =
    useState<string>('select-preference');
  const { user } = useUser();
  const { agents } = useAgents();
  const { saveAssistant } = useSaveAssistant();

  const handleSectionChange = (section: string, subSection?: string) => {
    setSelectedSection(section);
    if (subSection) {
      setSelectedSubSection(subSection);
    } else {
      // Set default subSection for each section
      const defaultSubSections: Record<string, string> = {
        'reservation-system-setup': 'select-preference',
        'assistant-definition': 'assistant-name',
        personalization: 'greeting',
        analytics: 'call-stage',
      };
      setSelectedSubSection(defaultSubSections[section]);
    }
  };

  const handleNext = async () => {
    const sections = [
      'reservation-system-setup',
      'assistant-definition',
      'personalization',
      'analytics',
    ];
    const currentIndex = sections.indexOf(selectedSection);
    if (currentIndex < sections.length - 1) {
      handleSectionChange(sections[currentIndex + 1]);
    }

    // Save assistant data
    await saveAssistant({
      userId: user?._id,
      assistantId: agents?.agents[agents.agents.length - 1]._id,
      businessId: user?.business?._id,
      status: 'draft',
      section: selectedSection as
        | 'assistant-definition'
        | 'personalization'
        | 'analytics'
        | 'reservation-system-setup',
    });
  };

  const handlePrev = () => {
    const sections = [
      'reservation-system-setup',
      'assistant-definition',
      'personalization',
      'analytics',
    ];
    const currentIndex = sections.indexOf(selectedSection);
    if (currentIndex > 0) {
      handleSectionChange(sections[currentIndex - 1]);
    }
  };

  const formatSectionTitle = (section: string) => {
    return section
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderContent = () => {
    switch (selectedSection) {
      case 'reservation-system-setup':
        return <ReservationSetup onNext={handleNext} />;
      case 'assistant-definition':
        return (
          <AssistantDefination
            onNext={handleNext}
            onPrev={handlePrev}
            isPending={false}
            shouldShowMenuConfigFlow={true}
          />
        );
      case 'personalization':
        return (
          <Personalization
            onNext={handleNext}
            onPrev={handlePrev}
            selectedSubSection={selectedSubSection}
            isPending={false}
          />
        );
      case 'analytics':
        return (
          <InitialAnalytics
            onPrev={handlePrev}
            shouldShowMenuConfigFlow={false}
          />
        );
      default:
        return null;
    }
  };

  return (
    <LayoutWithSidebar>
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          py: 0.2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderBottom: '1px solid #E0E0E0',
            p: 2,
            backgroundColor: 'white',
          }}
        >
          <Typography
            variant="h6"
            color="custom.textBlue"
            sx={{ textAlign: 'center' }}
          >
            <span
              style={{
                fontSize: '16px',
                fontWeight: 400,
                color: 'lightGray.text',
              }}
            >
              Set Up Reservation Assistant
            </span>
            <span
              style={{
                fontSize: '16px',
                margin: '0 8px',
                color: 'custom.textBlue',
              }}
            >
              /
            </span>
            <span
              style={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'custom.textBlue',
              }}
            >
              {formatSectionTitle(selectedSection)}
            </span>
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <ReservationSidebar
            templateName="Automate Reservation"
            selectedSection={selectedSection}
            selectedSubSection={selectedSubSection}
            onSectionChange={handleSectionChange}
          />

          <Box sx={{ flex: 1, overflow: 'auto', p: 0 }}>{renderContent()}</Box>
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default ReservationFlow;
